import {
  RushdMembershipStatus,
  RushdPricingQuote,
  RushdCheckoutSession,
  RushdBillingPortalSession,
  RushdApiResponse,
  RUSHD_API_ENDPOINTS,
} from '../types/rushd';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001';

class RushdApiService {
  
  /**
   * Get user's Rush'd membership status directly from Firebase
   */
  async getMembershipStatusFromFirebase(userId: string): Promise<RushdMembershipStatus> {
    try {
      if (!userId) {
        return {
          isMember: false,
          isActive: false,
        };
      }

      const membershipRef = doc(db, 'rush_d_memberships', userId);
      const membershipDoc = await getDoc(membershipRef);
      
      if (!membershipDoc.exists()) {
        return {
          isMember: false,
          isActive: false,
        };
      }
      
      const membership = membershipDoc.data();
      
      // Validate membership data structure
      if (!membership.currentPeriodEnd || !membership.status) {
        console.error(`Invalid membership data for user ${userId}:`, membership);
        return {
          isMember: false,
          isActive: false,
        };
      }
      
      // Convert Firestore Timestamp to Date
      const currentPeriodEnd = membership.currentPeriodEnd.toDate();
      const nextBillingDate = membership.nextBillingDate?.toDate();
      const now = new Date();
      
      // Determine if membership is active
      const isActive = membership.status === 'active' && currentPeriodEnd > now;
      
      return {
        isMember: true,
        isActive,
        status: membership.status,
        plan: membership.plan,
        currentPeriodEnd,
        nextBillingDate,
        cancelAtPeriodEnd: membership.cancelAtPeriodEnd || false,
        usageThisPeriod: membership.usageThisPeriod || 0,
        usageLimit: membership.usageLimit,
        stripeCustomerId: membership.stripeCustomerId,
        stripeSubscriptionId: membership.stripeSubscriptionId,
      };
    } catch (error) {
      console.error('Error getting membership status from Firebase:', error);
      // Return false status instead of throwing to avoid breaking the app
      return {
        isMember: false,
        isActive: false,
      };
    }
  }
  
  /**
   * Get user's Rush'd membership status (fallback to Firebase if API fails)
   */
  async getMembershipStatus(userId: string): Promise<RushdMembershipStatus> {
    try {
      const response = await fetch(`${API_BASE_URL}${RUSHD_API_ENDPOINTS.membership(userId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: RushdApiResponse<{ membership: RushdMembershipStatus }> = await response.json();
      
      if (!data.success || !data.data) {
        throw new Error(data.error?.message || 'Failed to get membership status');
      }
      
      // Convert date strings back to Date objects
      const membership = data.data.membership;
      if (membership.currentPeriodEnd && typeof membership.currentPeriodEnd === 'string') {
        membership.currentPeriodEnd = new Date(membership.currentPeriodEnd);
      }
      if (membership.nextBillingDate && typeof membership.nextBillingDate === 'string') {
        membership.nextBillingDate = new Date(membership.nextBillingDate);
      }
      
      return membership;
    } catch (error) {
      console.error('Error getting membership status from API, trying Firebase directly:', error);
      
      // Fallback to Firebase direct access
      try {
        return await this.getMembershipStatusFromFirebase(userId);
      } catch (firebaseError) {
        console.error('Error getting membership status from Firebase:', firebaseError);
        // Return default non-member status on error
        return {
          isMember: false,
          isActive: false,
        };
      }
    }
  }
  
  /**
   * Create Stripe Checkout session for Rush'd subscription
   */
  async createCheckoutSession(
    plan: 'monthly' | 'yearly',
    currency: 'usd' | 'cad' | 'gbp',
    userId: string,
    userEmail: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<RushdCheckoutSession> {
    try {
      const response = await fetch(`${API_BASE_URL}${RUSHD_API_ENDPOINTS.createCheckoutSession}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan,
          currency,
          userId,
          userEmail,
          successUrl,
          cancelUrl,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }
  
  /**
   * Create Stripe Billing Portal session for subscription management
   */
  async createBillingPortalSession(
    userId: string,
    returnUrl: string
  ): Promise<RushdBillingPortalSession> {
    try {
      const response = await fetch(`${API_BASE_URL}${RUSHD_API_ENDPOINTS.createBillingPortalSession}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          returnUrl,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating billing portal session:', error);
      throw error;
    }
  }
  
  /**
   * Get Express shipping pricing quote (member vs non-member)
   */
  async getExpressPricingQuote(
    cartItems: any[],
    shippingAddress: any,
    cartSubtotal: number,
    userId?: string
  ): Promise<RushdPricingQuote> {
    try {
      const response = await fetch(`${API_BASE_URL}${RUSHD_API_ENDPOINTS.quoteExpressShipping}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          cartItems,
          shippingAddress,
          cartSubtotal,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting Express pricing quote:', error);
      // Return a fallback quote on error
      return {
        regularPrice: 35.00, // Fallback price
        isMember: false,
        isEligible: false,
        reason: 'Unable to fetch pricing at this time',
      };
    }
  }
  
  /**
   * Get Rush'd plan configurations
   */
  async getPlans(): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}${RUSHD_API_ENDPOINTS.plans}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.plans;
    } catch (error) {
      console.error('Error getting Rush\'d plans:', error);
      throw error;
    }
  }
}

// Singleton instance
export const rushdApiService = new RushdApiService();

// Named exports for convenience
export const {
  getMembershipStatus,
  getMembershipStatusFromFirebase,
  createCheckoutSession,
  createBillingPortalSession,
  getExpressPricingQuote,
  getPlans,
} = rushdApiService;